// v050-g5 (B-503, B-504, B-509) — référentiel d'organisation, héritage des exigences d'un
// projet, dérogations et mode deux personnes. L'identité (demandeur, relecteur, décideur)
// vient TOUJOURS du jeton côté serveur : elle n'est jamais transmise ici.
import { request } from './http'
import type {
  InheritanceMode, OrgGovernanceSettings, OrgRequirement, OrgRequirementCreate, OrgRequirementStatus,
  PresenceCheckReport, ProjectCriterionCreate, ProjectGovernanceMode, ProjectRequirements,
  RequirementRemoval, RiskLevel, TwoPersonEligibility, TwoPersonModeState, Waiver, WaiverCreate, WaiverList,
} from './types/organisation'

const ws = (workspaceId: string) => `/workspaces/${encodeURIComponent(workspaceId)}`

export const organisationReferentialApi = {
  // ---- Référentiel de l'organisation (ORG-01) ----
  listOrgRequirements: () => request<OrgRequirement[]>('/org-requirements'),

  getOrgRequirement: (id: number) => request<OrgRequirement>(`/org-requirements/${id}`),

  createOrgRequirement: (body: OrgRequirementCreate) =>
    request<OrgRequirement>('/org-requirements', { method: 'POST', body: JSON.stringify(body) }),

  updateOrgRequirement: (id: number, patch: Partial<Pick<OrgRequirement, 'statement' | 'threshold'
    | 'measurement_method' | 'control_name' | 'owner' | 'generator_summary'>>) =>
    request<OrgRequirement>(`/org-requirements/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  changeOrgRequirementStatus: (id: number, status: OrgRequirementStatus) =>
    request<OrgRequirement>(`/org-requirements/${id}/status`,
      { method: 'POST', body: JSON.stringify({ status }) }),

  getOrgGovernanceSettings: () => request<OrgGovernanceSettings>('/org-governance/settings'),

  updateOrgGovernanceSettings: (newProjectMode: InheritanceMode, architectUserIds: string[]) =>
    request<OrgGovernanceSettings>('/org-governance/settings', {
      method: 'PUT',
      body: JSON.stringify({ new_project_mode: newProjectMode, architect_user_ids: architectUserIds }),
    }),

  // ---- Héritage du projet (ORG-02, ORG-04, ORG-10) ----
  listProjectRequirements: (workspaceId: string) =>
    request<ProjectRequirements>(`${ws(workspaceId)}/requirements`),

  /** v3-p03 (E16) : `proprietaire` (user_id, membre du projet) est obligatoire pour une règle propre au projet. */
  addProjectRequirement: (workspaceId: string, body: ProjectCriterionCreate & { proprietaire: string }) =>
    request<{ id: number; code: string; origin: 'added'; state: string }>(
      `${ws(workspaceId)}/requirements`, { method: 'POST', body: JSON.stringify(body) }),

  removeProjectRequirement: (workspaceId: string, requirementId: number) =>
    request<RequirementRemoval>(`${ws(workspaceId)}/requirements/${requirementId}`, { method: 'DELETE' }),

  setProjectInheritanceMode: (workspaceId: string, inheritanceMode: InheritanceMode, enforceAfter?: string) =>
    request<ProjectGovernanceMode>(`${ws(workspaceId)}/requirements/mode`, {
      method: 'PUT',
      body: JSON.stringify({ inheritance_mode: inheritanceMode, ...(enforceAfter ? { enforce_after: enforceAfter } : {}) }),
    }),

  /** ORG-AC-04 : contrôle déterministe de présence (rapport, sans effet). */
  checkRequirementPresence: (workspaceId: string, source: { content: string } | { approval_id: number }) =>
    request<PresenceCheckReport>(`${ws(workspaceId)}/requirements/presence-check`,
      { method: 'POST', body: JSON.stringify(source) }),

  // ---- Mode deux personnes (B-509) ----
  twoPersonMode: (workspaceId: string) =>
    request<TwoPersonModeState>(`${ws(workspaceId)}/governance/two-person-mode`),

  twoPersonEligibility: (approvalId: number) =>
    request<TwoPersonEligibility>(`/approvals/${approvalId}/two-person-eligibility`),

  raiseApprovalRiskCategory: (approvalId: number, riskCategory: RiskLevel) =>
    request<TwoPersonEligibility>(`/approvals/${approvalId}/risk-category`,
      { method: 'POST', body: JSON.stringify({ risk_category: riskCategory }) }),

  // ---- Dérogations (ORG-05, ORG-06) ----
  listWaivers: (workspaceId: string) => request<WaiverList>(`${ws(workspaceId)}/waivers`),

  requestWaiver: (workspaceId: string, body: WaiverCreate) =>
    request<Waiver>(`${ws(workspaceId)}/waivers`, { method: 'POST', body: JSON.stringify(body) }),

  reviewWaiver: (workspaceId: string, waiverId: number, verdict: 'approve' | 'reject', comment: string) =>
    request<Waiver>(`${ws(workspaceId)}/waivers/${waiverId}/review`,
      { method: 'POST', body: JSON.stringify({ verdict, comment }) }),

  decideWaiver: (workspaceId: string, waiverId: number, decision: 'grant' | 'refuse', comment?: string) =>
    request<Waiver>(`${ws(workspaceId)}/waivers/${waiverId}/decision`,
      { method: 'POST', body: JSON.stringify({ decision, ...(comment ? { comment } : {}) }) }),

  liftWaiver: (workspaceId: string, waiverId: number) =>
    request<Waiver>(`${ws(workspaceId)}/waivers/${waiverId}/lift`, { method: 'POST' }),
}
